
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { IconCode, IconCpu, IconSearch, IconSparkles, IconQuant } from '../components/Icons';
import { generateSyntheticHistory, calculateSMA, runBacktest } from '../services/mathEngine';
import { optimizeAlgoStrategy } from '../services/geminiService';
import toast from 'react-hot-toast';

const AlgoView: React.FC = () => {
    // Inputs
    const [asset, setAsset] = useState('BTC/USD');
    const [smaShort, setSmaShort] = useState(20);
    const [smaLong, setSmaLong] = useState(50);
    
    // Data & Results
    const [chartData, setChartData] = useState<any[]>([]);
    const [backtestStats, setBacktestStats] = useState<any>(null);
    const [aiFeedback, setAiFeedback] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false);

    const handleRunBacktest = async () => {
        setIsRunning(true);
        setAiFeedback('');
        
        // Simulate processing delay
        await new Promise(r => setTimeout(r, 800));

        // 1. Generate Market Data (Simulated)
        const historyLen = 252; // 1 Year
        const prices = generateSyntheticHistory(45000, 0.60, 0.20, historyLen); // BTC-like volatility
        
        // 2. Calculate Indicators
        const smaS = calculateSMA(prices, smaShort);
        const smaL = calculateSMA(prices, smaLong);

        // 3. Run Backtest Engine
        const { equityCurve, trades } = runBacktest(prices, smaS as number[], smaL as number[]);

        // 4. Prepare Chart Data
        const combinedData = prices.map((p, i) => ({
            day: i,
            price: p,
            equity: equityCurve[i],
            smaShort: smaS[i] || p,
            smaLong: smaL[i] || p
        }));
        
        setChartData(combinedData);

        // 5. Calculate Stats
        const startEq = equityCurve[0];
        const endEq = equityCurve[equityCurve.length - 1];
        const ret = ((endEq - startEq) / startEq) * 100;
        
        setBacktestStats({
            totalReturn: ret.toFixed(2),
            trades: trades,
            finalEquity: endEq.toFixed(0)
        });

        setIsRunning(false);
        toast.success("Backtest Complete");
    };

    const handleAiOptimize = async () => {
        if (!backtestStats) return;
        setIsOptimizing(true);
        const feedback = await optimizeAlgoStrategy(
            { asset, smaShort, smaLong }, 
            backtestStats
        );
        setAiFeedback(feedback);
        setIsOptimizing(false);
    };

    return (
        <div className="flex h-full bg-slate-50">
            {/* Left: Strategy Editor */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                    <IconCode className="text-violet-600" /> Algo Studio
                </h2>
                <p className="text-xs text-slate-500 mb-6">No-Code Strategy Builder</p>

                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Target Asset</label>
                        <div className="relative">
                            <input 
                                type="text" 
                                value={asset}
                                onChange={e => setAsset(e.target.value)}
                                className="w-full border border-slate-300 rounded p-2 pl-8 text-sm outline-none focus:border-blue-500"
                            />
                            <span className="absolute left-2.5 top-2.5 text-slate-400"><IconSearch /></span>
                        </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                        <h3 className="text-xs font-bold text-slate-700 mb-3 uppercase">Logic: SMA Crossover</h3>
                        
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-slate-500 mb-1">Fast Period (SMA)</label>
                                <input 
                                    type="number" 
                                    value={smaShort}
                                    onChange={e => setSmaShort(Number(e.target.value))}
                                    className="w-full border p-1.5 rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-500 mb-1">Slow Period (SMA)</label>
                                <input 
                                    type="number" 
                                    value={smaLong}
                                    onChange={e => setSmaLong(Number(e.target.value))}
                                    className="w-full border p-1.5 rounded text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleRunBacktest}
                        disabled={isRunning}
                        className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                    >
                        {isRunning
                            ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                            : <IconCpu />}
                        {isRunning ? 'Simulating...' : 'Run Backtest'}
                    </button>
                </div>
                
                {backtestStats && (
                    <div className="mt-8 border-t border-slate-100 pt-6">
                        <button
                            onClick={handleAiOptimize}
                            disabled={isOptimizing}
                            className="w-full py-2 bg-violet-50 text-violet-700 border border-violet-200 font-bold rounded-lg text-xs flex items-center justify-center gap-2 hover:bg-violet-100 transition-colors disabled:opacity-60"
                        >
                            {isOptimizing
                                ? <span className="w-3 h-3 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin"/>
                                : <IconSparkles />}
                            Optimize with AiQ
                        </button>
                        
                        {aiFeedback && (
                            <div className="mt-4 bg-slate-800 text-slate-200 p-3 rounded text-xs leading-relaxed border border-slate-700">
                                <div className="font-bold text-purple-400 mb-1 flex items-center gap-1"><IconQuant /> Recommendation</div>
                                {aiFeedback}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right: Results Visualization */}
            <div className="flex-1 p-6 flex flex-col overflow-hidden">
                {chartData.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                        <div className="p-8 text-center max-w-sm">
                            <div className="w-16 h-16 rounded-2xl bg-violet-50 border border-violet-100 flex items-center justify-center mx-auto mb-4">
                                <IconCode className="text-violet-400" />
                            </div>
                            <p className="text-base font-semibold text-slate-600 mb-1">No results yet</p>
                            <p className="text-sm text-slate-400">Configure your strategy parameters on the left, then click <span className="font-semibold text-violet-600">Run Backtest</span> to see the equity curve and performance stats.</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col space-y-4 overflow-y-auto custom-scrollbar">
                        {/* Stats Bar */}
                        <div className="grid grid-cols-3 gap-4 shrink-0">
                            <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                                <div className="text-xs text-slate-500 uppercase font-bold">Total Return</div>
                                <div className={`text-2xl font-bold ${Number(backtestStats.totalReturn) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {Number(backtestStats.totalReturn) > 0 ? '+' : ''}{backtestStats.totalReturn}%
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                                <div className="text-xs text-slate-500 uppercase font-bold">Final Equity</div>
                                <div className="text-2xl font-bold text-slate-800">${Number(backtestStats.finalEquity).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                                <div className="text-xs text-slate-500 uppercase font-bold">Trades Executed</div>
                                <div className="text-2xl font-bold text-violet-600">{backtestStats.trades}</div>
                            </div>
                        </div>

                        {/* Charts */}
                        <div className="bg-white p-4 rounded border border-slate-200 shadow-sm flex-1 min-h-[300px]">
                            <h3 className="text-sm font-bold text-slate-700 mb-4">Equity Curve vs. Buy & Hold</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="day" fontSize={12} stroke="#94a3b8" />
                                    <YAxis yAxisId="left" fontSize={12} stroke="#94a3b8" />
                                    <YAxis yAxisId="right" orientation="right" fontSize={12} stroke="#94a3b8" />
                                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} name="Strategy Equity" dot={false} />
                                    <Line yAxisId="right" type="monotone" dataKey="price" stroke="#cbd5e1" strokeWidth={2} name="Asset Price" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        
                        <div className="bg-white p-4 rounded border border-slate-200 shadow-sm h-[200px] shrink-0">
                            <h3 className="text-sm font-bold text-slate-700 mb-4">Indicator Check (SMA {smaShort} vs {smaLong})</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData.slice(-50)}> 
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="day" fontSize={10} stroke="#94a3b8" />
                                    <YAxis domain={['auto', 'auto']} fontSize={10} stroke="#94a3b8" />
                                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                                    <Line type="monotone" dataKey="price" stroke="#94a3b8" strokeWidth={1} dot={false} />
                                    <Line type="monotone" dataKey="smaShort" stroke="#3b82f6" strokeWidth={2} dot={false} name="Fast SMA" />
                                    <Line type="monotone" dataKey="smaLong" stroke="#f59e0b" strokeWidth={2} dot={false} name="Slow SMA" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AlgoView;
